////////////////////////////////////////
// IMPORTING MODULES
////////////////////////////////////////

"use client";

import { useState } from "react";
import axios from "axios";
import { ArrowLeft, Trash2, CheckCircle, AlertCircle } from "lucide-react";
import { useNavigate } from "react-router-dom";

const GOOGLE_FORM_LINK =
  "https://docs.google.com/forms/d/e/1FAIpQLSepLul0w7QkBalvT2riMjYhek6lrxMIaRhJqEgtMDlHO6_Nvw/formResponse";
const GOOGLE_FORM_NAME_ID = "entry.1782811584";
const GOOGLE_FORM_EMAIL_ID = "entry.1796655042";
const GOOGLE_FORM_MESSAGE_ID = "entry.566797207";

////////////////////////////////////////
// COMPONENT: DELETE ACCOUNT
////////////////////////////////////////

const DeleteAccount: React.FC = () => {
  const navigate = useNavigate();

  const [email, setEmail] = useState("");
  const [confirmEmail, setConfirmEmail] = useState("");
  const [submitted, setSubmitted] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  const validateEmail = (email: string): boolean => {
    const re = /\S+@\S+\.\S+/;
    return re.test(email);
  };

  const handleSubmit = () => {
    setError("");

    if (!email.trim()) {
      setError("Please enter the email associated with your account.");
      return;
    }

    if (!validateEmail(email)) {
      setError("Please enter a valid email address.");
      return;
    }

    if (email !== confirmEmail) {
      setError("Please make sure both email fields match.");
      return;
    }

    setLoading(true);
    const formData = new FormData();

    formData.append(GOOGLE_FORM_NAME_ID, "Account Deletion Request");
    formData.append(GOOGLE_FORM_EMAIL_ID, email);
    formData.append(
      GOOGLE_FORM_MESSAGE_ID,
      `ACCOUNT DELETION REQUEST\n\nUser has requested complete deletion of their Flight Trail account and all associated data.\n\nEmail: ${email}\n\nPlease process this request within 30 days as per the Privacy Policy.`
    );

    axios
      .post(GOOGLE_FORM_LINK, formData)
      .then(() => setSubmitted(true))
      .catch(() => setSubmitted(true)) // Google Forms CORS workaround
      .finally(() => setLoading(false));
  };

  return (
    <div className="min-h-screen bg-[#0e1220] text-gray-100 overflow-x-hidden">
      <header className="border-b border-gray-700/50 bg-[#0e1220]/80 backdrop-blur-sm sticky top-0 z-40">
        <div className="px-4 py-3 sm:py-4 flex items-center justify-between">
          <div className="flex items-center space-x-2 sm:space-x-4">
            <button
              onClick={() => navigate("/")}
              className="flex items-center gap-2 px-3 py-2 rounded-lg hover:bg-gray-800 transition-colors text-gray-300 hover:text-gray-100"
            >
              <ArrowLeft className="h-4 w-4" />
              <span className="hidden sm:inline">Back</span>
            </button>
          </div>
        </div>
      </header>

      <div className="max-w-2xl mx-auto px-6 py-8">
        {/* Header */}
        <div className="text-center mb-8">
          <div className="inline-flex items-center justify-center w-16 h-16 rounded-full bg-red-500/10 mb-4">
            <Trash2 className="h-8 w-8 text-red-400" />
          </div>
          <h1 className="font-bold text-3xl mb-2 text-gray-100">
            Delete Your Account
          </h1>
          <p className="text-gray-400">
            Request permanent deletion of your Flight Trail account
          </p>
        </div>

        {submitted ? (
          <div className="flex flex-col items-center justify-center py-12">
            <div className="bg-green-500/10 rounded-full p-4 mb-4">
              <CheckCircle className="h-12 w-12 text-green-400" />
            </div>
            <h3 className="text-xl font-semibold mb-2 text-gray-100">
              Request Submitted
            </h3>
            <p className="text-gray-400 text-center max-w-md mb-6">
              We've received your account deletion request. Your account and all
              associated data will be permanently deleted within 30 days. You'll
              receive a confirmation email once the process is complete.
            </p>
            <button
              onClick={() => navigate("/")}
              className="px-6 py-2 bg-gray-700 hover:bg-gray-600 rounded-lg transition-colors"
            >
              Return Home
            </button>
          </div>
        ) : (
          <>
            {/* What Gets Deleted */}
            <div className="bg-[#1a2235] border border-gray-700/50 rounded-lg p-6 mb-6">
              <h2 className="font-semibold text-lg mb-4 text-gray-100">
                What will be deleted
              </h2>
              <ul className="space-y-3 text-sm text-gray-300">
                <li className="flex items-start gap-3">
                  <CheckCircle className="h-5 w-5 text-red-400 flex-shrink-0 mt-0.5" />
                  <span>Your account credentials and email address</span>
                </li>
                <li className="flex items-start gap-3">
                  <CheckCircle className="h-5 w-5 text-red-400 flex-shrink-0 mt-0.5" />
                  <span>All saved airports and travel history</span>
                </li>
                <li className="flex items-start gap-3">
                  <CheckCircle className="h-5 w-5 text-red-400 flex-shrink-0 mt-0.5" />
                  <span>Your travel statistics and preferences</span>
                </li>
                <li className="flex items-start gap-3">
                  <CheckCircle className="h-5 w-5 text-red-400 flex-shrink-0 mt-0.5" />
                  <span>All other data associated with your account</span>
                </li>
              </ul>
            </div>

            {/* Important Notice */}
            <div className="bg-amber-500/10 border border-amber-500/30 rounded-lg p-4 mb-6">
              <div className="flex gap-3">
                <AlertCircle className="h-5 w-5 text-amber-400 flex-shrink-0 mt-0.5" />
                <div>
                  <h3 className="font-semibold text-amber-400 mb-1">
                    Important
                  </h3>
                  <p className="text-sm text-gray-300">
                    This action is permanent and cannot be undone. Once your
                    account is deleted, all your travel data will be permanently
                    removed from our servers and cannot be recovered.
                  </p>
                </div>
              </div>
            </div>

            {/* Retention Notice */}
            <div className="bg-[#1a2235] border border-gray-700/50 rounded-lg p-6 mb-6">
              <h2 className="font-semibold text-lg mb-3 text-gray-100">
                Data Retention
              </h2>
              <p className="text-sm text-gray-300 mb-3">
                Your personal data will be permanently deleted within{" "}
                <strong className="text-gray-100">30 days</strong> of your
                request.
              </p>
              <p className="text-sm text-gray-400">
                We may retain certain data if required for legal compliance,
                fraud prevention, or security purposes. In such cases, data will
                be retained only as long as necessary and you will be informed.
              </p>
            </div>

            {/* Deletion Form */}
            <div className="bg-[#1a2235] border border-gray-700/50 rounded-lg p-6">
              <h2 className="font-semibold text-lg mb-4 text-gray-100">
                Request Account Deletion
              </h2>
              <p className="text-sm text-gray-400 mb-6">
                Enter the email address associated with your Flight Trail
                account to submit a deletion request.
              </p>

              {error && (
                <div className="bg-red-500/10 border border-red-500/30 rounded-lg p-3 mb-4">
                  <p className="text-sm text-red-400">{error}</p>
                </div>
              )}

              <form
                onSubmit={(e) => {
                  e.preventDefault();
                  handleSubmit();
                }}
                className="space-y-4"
              >
                <div>
                  <label className="block text-sm font-medium text-gray-300 mb-2">
                    Account Email
                  </label>
                  <input
                    type="email"
                    placeholder="Enter your account email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    className="w-full py-3 px-4 bg-[#0e1220] border border-gray-700 rounded-md text-gray-100 placeholder:text-gray-500 focus:outline-none focus:ring-2 focus:ring-cyan-500 focus:border-transparent"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-300 mb-2">
                    Confirm Email
                  </label>
                  <input
                    type="email"
                    placeholder="Confirm your email"
                    value={confirmEmail}
                    onChange={(e) => setConfirmEmail(e.target.value)}
                    className="w-full py-3 px-4 bg-[#0e1220] border border-gray-700 rounded-md text-gray-100 placeholder:text-gray-500 focus:outline-none focus:ring-2 focus:ring-cyan-500 focus:border-transparent"
                  />
                </div>

                <button
                  type="submit"
                  disabled={loading}
                  className="w-full py-3 px-4 bg-red-600 hover:bg-red-700 disabled:bg-red-800 disabled:cursor-not-allowed text-white font-medium rounded-md transition-colors"
                >
                  {loading ? "Submitting..." : "Request Account Deletion"}
                </button>
              </form>
            </div>

            {/* Alternative Contact */}
            <p className="text-center text-sm text-gray-500 mt-6">
              Having trouble? Contact us directly at{" "}
              <a
                href="mailto:flighttrailapp@gmail.com?subject=Account%20Deletion%20Request"
                className="text-cyan-400 hover:underline"
              >
                flighttrailapp@gmail.com
              </a>
            </p>
          </>
        )}
      </div>
    </div>
  );
};

////////////////////////////////////////
// EXPORTING DELETE ACCOUNT
////////////////////////////////////////

export default DeleteAccount;
